import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Phone, Mail, ChevronDown } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

const Particles = () => {
  const bits = Array.from({ length: 26 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {bits.map((_, i) => {
        const left = Math.random() * 100;
        const dur = 14 + Math.random() * 16;
        const delay = Math.random() * 14;
        const scale = 0.6 + Math.random() * 1.1;
        return (
          <span
            key={i}
            className="rice-particle"
            style={{
              left: `${left}%`,
              animationDuration: `${dur}s`,
              animationDelay: `${delay}s`,
              transform: `scale(${scale})`,
            }}
          />
        );
      })}
    </div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollToAbout = () => {
    const el = document.getElementById("about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const btns = [
    { label: t("hero.whatsappBtn"), icon: MessageCircle, href: `https://wa.me/${CONTACT.whatsapp}?text=Hello%20Gupta%20Enterprises%2C%20I%27d%20like%20to%20enquire%20about%20Vasavi%20Gold%20rice.`, primary: true, testid: "hero-whatsapp-btn" },
    { label: t("hero.callBtn"), icon: Phone, href: `tel:${CONTACT.phoneDial}`, testid: "hero-call-btn" },
    { label: t("hero.enquiryBtn"), icon: Mail, href: "#contact", testid: "hero-enquiry-btn" },
  ];

  const headlineLines = [
    t("hero.headlineLine1"),
    t("hero.headlineLine2"),
    t("hero.headlineLine3"),
  ];

  return (
    <section id="hero" ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.heroField}
          alt="Sunrise over lush green rice fields"
          className="h-[120%] w-full object-cover"
          fetchPriority="high"
        />
        {/* Light, brand-tinted wash — keeps it airy, never dark */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#f8fff8]/85 via-[#f8fff8]/45 to-[#f8fff8]/92" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#f8fff8]/70 via-transparent to-transparent" />
      </motion.div>

      <Particles />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="flex items-center gap-3 mb-7"
        >
          <span className="h-px w-10 bg-[#d4af37]" />
          <span className="eyebrow">{t("hero.since")}</span>
        </motion.div>

        <h1 className="font-display font-semibold text-[#1c2b22] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.98] max-w-5xl">
          {headlineLines.map((line, i) => (
            <span className="reveal-mask" key={i}>
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.35 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? (
                  <>
                    {line}<span className="text-[#40916c]">.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="font-body text-[#33463b] text-base md:text-lg leading-relaxed max-w-xl mt-8"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="flex flex-wrap items-center gap-4 mt-10"
        >
          {btns.map((b) => {
            const Icon = b.icon;
            return (
              <a
                key={b.label}
                href={b.href}
                target={b.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className={`inline-flex items-center gap-2.5 rounded-full font-body font-medium text-[15px] px-7 py-3.5 transition-all hover:-translate-y-0.5 ${
                  b.primary
                    ? "bg-[#40916c] text-white hover:bg-[#2d6a4f] hover:shadow-xl hover:shadow-[#40916c]/25"
                    : "bg-white/70 backdrop-blur-md text-[#2d6a4f] border border-[#40916c]/25 hover:bg-white hover:border-[#40916c]/50"
                }`}
                data-testid={b.testid}
              >
                <Icon size={18} strokeWidth={2} />
                {b.label}
              </a>
            );
          })}
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollToAbout}
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 group"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll down"
      >
        <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#40916c]/70">{t("hero.scroll")}</span>
        <span className="relative h-9 w-5 rounded-full border border-[#40916c]/40 flex justify-center pt-1.5">
          <span className="scroll-dot h-1.5 w-1.5 rounded-full bg-[#40916c]" />
        </span>
        <ChevronDown size={16} className="text-[#40916c]/50 -mt-1 group-hover:translate-y-0.5 transition-transform" />
      </motion.button>
    </section>
  );
};

export default Hero;
